import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { _get, _put } from "../../../api/chatbotApiClient";

export const initialState = {
  chatbotHistory: [],
  loading: false,
  error: null,
  page: 1,
  messages: [],
  chatbotIsStreaming: false,
  threadDetails: {},
  buyConsultation: false,
};

export const chatbotHistoryThunk = createAsyncThunk(
  "chatbot/fetchHistory",
  async ({ page = 1 }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();

      const _orgClientId =
        state.orgReducer.selectedFI.orgClientId ??
        state.authReducer?.userData?.client_Id;

      const response = await _get(
        `${_orgClientId}/chat?PageNumber=${page}&PageSize=5`,
        {},
        {},
        {},
      );

      const formattedHistory = (response?.data?.data?.data ?? []).flatMap(
        (item) => {
          const formattedTime = new Date(item.createdOn).toLocaleString(
            "en-US",
            {
              weekday: "long",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            },
          );

          return [
            { sender: "user", text: item.message, createdOn: formattedTime },
            { sender: "bot", text: item.response, createdOn: formattedTime },
          ];
        },
      );

      return { data: formattedHistory, page };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to load chat history",
      );
    }
  },
);

export const fetchThreadMessagesThunk = createAsyncThunk(
  "chatbot/fetchThreadMessages",
  async ({ threadId, page = 1 }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();

      const _orgClientId =
        state.orgReducer.selectedFI.orgClientId ??
        state.authReducer?.userData?.client_Id;

      const response = await _get(
        `${_orgClientId}/chat/${threadId}?PageSize=10&PageNumber=${page}`,
        {},
        {},
        {},
      );

      const formattedMessages = (response?.data?.data?.data ?? [])
        .reverse()
        .flatMap((item) => {
          return [
            {
              id: `${item.id}-user`,
              sender: "user",
              message: item.message,
              dateTime: item.createdOn,
            },

            {
              id: `${item.id}-bot`,
              sender: "bot",
              message: item.response || item.reasponse,
              dateTime: item.createdOn,
              feedbackId: item.id,
              isLike: item.feedback,
            },
          ];
        });

      return { data: formattedMessages, page };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to load thread messages",
      );
    }
  },
);

// ================= FEEDBACK THUNK =================
export const chatbotFeedbackThunk = createAsyncThunk(
  "chatbot/chatbotFeedback",
  async ({ id, isLike }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();

      const _orgClientId =
        state.orgReducer.selectedFI.orgClientId ??
        state.authReducer?.userData?.client_Id;

      const response = await _put(
        `${_orgClientId}/chat/feedback?id=${id}&is_like=${isLike}`,
        {},
        {},
        {},
      );

      return {
        id,
        isLike,
        data: response?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.message || "Failed to submit feedback",
      );
    }
  },
);

export const chatbotSlice = createSlice({
  name: "chatbotSlice",
  initialState,
  reducers: {
    setChatbotHistory: (state, action) => {
      state.chatbotHistory.push(...action.payload); // add new messages
    },
    resetChatbotHistory: (state, action) => {
      return initialState;
    },
    addUserMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    addBotMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    setStreaming: (state, action) => {
      state.chatbotIsStreaming = action.payload;
    },
    clearChat: (state) => {
      state.messages = [];
      state.threadDetails = {};
      state.chatbotIsStreaming = false;
      state.loading = false;
      state.error = null;
    },

    updateMessageFeedback: (state, action) => {
      const { feedbackId, isLike } = action.payload;

      const message = state.messages.find(
        (msg) => msg.feedbackId === feedbackId,
      );

      if (message) {
        message.isLike = isLike;
      }
    },
    setThreadDetails: (state, action) => {
      state.threadDetails = action.payload;
    },
    setBuyConsultation: (state, action) => {
      state.buyConsultation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(chatbotHistoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(chatbotHistoryThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { data, page } = action.payload;
        if (page === 1) {
          state.chatbotHistory = data;
        } else {
          state.chatbotHistory = [...data, ...state.chatbotHistory];
        }

        state.page = page;
      })
      .addCase(chatbotHistoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchThreadMessagesThunk.pending, (state) => {
        state.chatbotIsStreaming = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchThreadMessagesThunk.fulfilled, (state, action) => {
        state.chatbotIsStreaming = false;
        state.loading = false;

        const { data, page } = action.payload;

        if (page === 1) {
          state.messages = data; // replace messages when new thread selected
        } else {
          state.messages = [...data, ...state.messages]; // pagination
        }
      })
      .addCase(fetchThreadMessagesThunk.rejected, (state, action) => {
        state.chatbotIsStreaming = false;
        state.loading = false;
        state.error = action.payload;
      })

      // ================= FEEDBACK =================
      .addCase(chatbotFeedbackThunk.pending, (state) => {
        state.loading = true;
      })

      .addCase(chatbotFeedbackThunk.fulfilled, (state, action) => {
        state.loading = false;

        const { id, isLike } = action.payload;

        state.messages = state.messages.map((msg) => {
          if (msg.feedbackId === id) {
            return {
              ...msg,
              isLike,
            };
          }
          return msg;
        });
      })
      .addCase(chatbotFeedbackThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setChatbotHistory,
  resetChatbotHistory,
  addUserMessage,
  addBotMessage,
  setStreaming,
  clearChat,
  updateMessageFeedback,
  setThreadDetails,
  setBuyConsultation,
} = chatbotSlice.actions;
export default chatbotSlice.reducer;
